import React, { useMemo } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';

/**
 * WelcomeMessage component that displays a time-aware greeting
 * with premium styling and personalization
 */
const WelcomeMessage = ({ userName = 'Member' }) => {
    // Array of inspiring quotes
    const inspiringQuotes = useMemo(() => [
        "You're part of something special. Every session brings you closer to your goals.",
        "Progress, not perfection. Every step forward is a victory worth celebrating.",
        "Your commitment today is building the strength you'll have tomorrow.",
        "Champions are made in moments like these. Keep showing up.",
        "The only bad workout is the one that didn't happen. You're here, and that's what counts.",
        "Your future self will thank you for the effort you're putting in today.",
        "Small daily improvements are the key to staggering long-term results.",
        "You don't have to be great to start, but you have to start to be great.",
        "Every session is an investment in the best version of yourself.",
        "Success is the sum of small efforts repeated day after day.",
        "Your dedication is shaping not just your body, but your character.",
        "The difference between who you are and who you want to be is what you do.",
        "You're not just training your body, you're training your mind to overcome limits.",
        "Believe in yourself. You're stronger than you think, capable of more than you know.",
        "Excellence is not a destination, it's a continuous journey. You're on that path.",
        "Your consistency is your superpower. Keep building on it.",
        "Every rep, every session, every choice—they all add up to something amazing.",
        "You're creating a legacy of discipline and determination with every visit.",
        "The pain you feel today will be the strength you feel tomorrow.",
        "You're not competing with others, you're becoming better than yesterday's version of yourself."
    ], []);

    // Select a random quote on component mount
    const inspiringMessage = useMemo(() => {
        return inspiringQuotes[Math.floor(Math.random() * inspiringQuotes.length)];
    }, [inspiringQuotes]);

    // Calculate time period once and memoize
    const timePeriod = useMemo(() => {
        const hour = new Date().getHours();
        
        if (hour >= 5 && hour < 12) {
            return 'morning';
        } else if (hour >= 12 && hour < 17) {
            return 'afternoon';
        } else if (hour >= 17 && hour < 22) {
            return 'evening';
        } else {
            return 'night';
        }
    }, []);

    // Get greeting based on time period
    const getGreeting = () => {
        switch (timePeriod) {
            case 'morning':
                return 'Good Morning';
            case 'afternoon':
                return 'Good Afternoon';
            case 'evening':
                return 'Good Evening';
            case 'night':
            default:
                return 'Welcome Back';
        }
    };

    // Get personalized message based on time period
    const getPersonalizedMessage = () => {
        switch (timePeriod) {
            case 'morning':
                return 'Ready to start your day strong?';
            case 'afternoon':
                return 'Keep up the excellent momentum!';
            case 'evening':
                return 'Time to wind down with excellence.';
            case 'night':
            default:
                return "We're here for you, anytime.";
        }
    };

    const greeting = getGreeting();
    const personalizedMessage = getPersonalizedMessage();

    return (
        <div className="relative overflow-hidden rounded-2xl card-luxury p-6 sm:p-8 mb-6 sm:mb-8 shadow-luxury-xl">
            {/* Premium gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/10 via-luxury-charcoal to-luxury-obsidian"></div>
            
            {/* Decorative background pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(16, 185, 129, 0.3) 1px, transparent 0)`,
                    backgroundSize: '32px 32px'
                }}></div>
            </div>
            
            {/* Animated sparkle effect */}
            <div className="absolute top-4 right-4 animate-pulse">
                <SparklesIcon className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-400 opacity-70" />
            </div>
            
            {/* Content */}
            <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-gradient-emerald">
                        {greeting}, <span className="text-emerald-200 font-extrabold">{userName}</span>
                    </h2>
                </div>
                
                <p className="text-base sm:text-lg text-accent-platinum font-body font-medium">
                    {personalizedMessage}
                </p>
                
                <div className="mt-4 pt-4 border-t border-emerald-600/20">
                    <p className="text-sm text-emerald-300/80 italic font-body">
                        ✨ {inspiringMessage}
                    </p>
                </div>
            </div>
            
            {/* Subtle shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-400/10 to-transparent opacity-30 transform -skew-x-12 animate-shimmer pointer-events-none"></div>
        </div>
    );
};

export default WelcomeMessage;
